import { NumberFormatter } from "@internationalized/number";
import React, {
  CSSProperties,
  use,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useState,
} from "react";
import { mergeProps } from "react-aria";
import { useEditor } from "tldraw";

import {
  BlockContainerShapeContext,
  ColorContext,
  theme,
} from "@/components/canvas-editor/components/Container";
import {
  ComparisonOperator,
  comparisonOperatorOptions,
  fieldOptions,
  percentComparisonOperatorOptions,
} from "@/components/canvas-editor/shapes/shapes";
import {
  CustomNodeEditorContext,
  preventBubble,
  useIsReadonlyEditor,
} from "@/components/canvas-editor/utils";
import { useActiveCustomTool } from "@/components/hooks/useCustomActiveTool";
import { Button, ButtonProps } from "@/components/ui/button";
import {
  Input,
  InputProps,
  NumberInput,
  NumberInputProps,
} from "@/components/ui/input";
import { SimpleSelect, SimpleSelectProps } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea, TextareaProps } from "@/components/ui/textarea";
import { Toggle, ToggleProps } from "@/components/ui/toggle";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { removeInvisibleChars } from "@/lib/remove-invisible-chars";
import { cn } from "@/lib/utils";

const percentFormatter = new NumberFormatter("en-US", { style: "percent" });

function DisplayValue({
  className,
  truncate = true,
  ...props
}: React.ComponentProps<"div"> & { truncate?: boolean }) {
  const color = use(ColorContext);

  return (
    <div
      className={cn(
        "px-3 py-2 h-10 rounded-md text-sm",
        truncate && "truncate",
        "bg-slate-subtle text-slate-normal border",
        theme.input[color || "default"],
        className,
      )}
      {...props}
    />
  );
}

const emptyArray: string[] = [];

export const CanvasSimpleSelect = function CanvasSimpleSelect({
  ref,
  value,
  onValueChange,
  options = emptyArray,
  ...props
}: SimpleSelectProps) {
  const isReadOnly = useIsReadonlyEditor();
  const id = useId();
  const activeCustomTool = useActiveCustomTool();
  const editor = useEditor();
  const shape = use(BlockContainerShapeContext);
  const [open, setOpen] = useState(false);
  const onOpenChange = useCallback(
    (open: boolean) => {
      setOpen(open);
      if (open) {
        editor.menus.addOpenMenu(id);
      } else {
        editor.menus.deleteOpenMenu(id);
      }
    },
    [editor.menus, id],
  );

  // A stored value that is no longer among `options` still has to render (and
  // save) -- so prepend it. Build the augmented list into a *new* array: most
  // call sites pass a shared module-level constant (e.g. `hourOptions`), and
  // mutating it would leak the stray value into every other node's dropdown.
  const resolvedOptions = useMemo<SimpleSelectProps["options"]>(() => {
    if (
      !value ||
      options.find(
        (o) =>
          (typeof o === "string" && o === value) ||
          (typeof o === "object" && "value" in o && o.value === value),
      )
    ) {
      return options;
    }

    if (typeof options[0] === "string") {
      return [value, ...(options as readonly string[])];
    }

    return [
      { value, label: value },
      ...(options as Array<{ value: string; label: React.ReactNode }>),
    ];
  }, [options, value]);

  useEffect(() => {
    if (!open) {
      return;
    }

    function onGlobalPointerDown(e: PointerEvent) {
      if (!e.target) {
        return;
      }

      if (
        e.target instanceof HTMLElement &&
        !e.target.closest(`[data-radix-select-viewport]`)
      ) {
        onOpenChange(false);
      }
    }

    document.addEventListener("pointerdown", onGlobalPointerDown, {
      capture: true,
    });

    return () => {
      document.removeEventListener("pointerdown", onGlobalPointerDown, {
        capture: true,
      });
    };
  }, [open, onOpenChange]);

  if (isReadOnly) {
    const displayName =
      resolvedOptions
        .filter(
          (o): o is { value: string; label: string } =>
            typeof o === "object" && "value" in o,
        )
        .find((o) => o.value === value)?.label || value;
    const display = (
      <DisplayValue
        ref={ref as unknown as React.Ref<HTMLDivElement>}
        className={props.className}
      >
        {displayName}
      </DisplayValue>
    );

    if (props.tooltip) {
      return <SimpleTooltip message={props.tooltip}>{display}</SimpleTooltip>;
    }

    return display;
  }

  const onInteract = (e: React.PointerEvent | React.TouchEvent) => {
    const targetShape = editor.getShapeAtPoint(editor.inputs.currentPagePoint);

    if (shape && targetShape && shape.id !== targetShape.id) {
      e.preventDefault();
    } else {
      e.stopPropagation();
    }
  };

  return (
    <SimpleSelect
      value={value}
      options={resolvedOptions}
      onValueChange={onValueChange}
      ref={ref}
      required={true}
      {...preventBubble}
      {...mergeProps(props, {
        onPointerDown: onInteract,
        onTouchStart: onInteract,
      })}
      open={open}
      onOpenChange={onOpenChange}
      className={cn(
        activeCustomTool ? "pointer-events-none" : "pointer-events-auto",
        props.className,
      )}
    />
  );
};

export const CanvasInput = function CanvasInput({
  ref,
  value,
  onChange,
  onValueChange: onValueChangeProp,
  ...props
}: InputProps & {
  "aria-label"?: string;
}) {
  const editor = useEditor();
  const shape = use(BlockContainerShapeContext);
  const onValueChange = useCallback(
    (newValue: string) => {
      if (props.type === "number") {
        onValueChangeProp?.(newValue || `${props.min || 0}`);
      } else {
        const sanitizedValue = removeInvisibleChars(newValue);
        onValueChangeProp?.(sanitizedValue);
      }
    },
    [onValueChangeProp, props.type, props.min],
  );
  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter") {
        event.preventDefault();
      }
    },
    [],
  );

  const isReadOnly = useIsReadonlyEditor();
  const activeCustomTool = useActiveCustomTool();

  if (isReadOnly) {
    return (
      <DisplayValue
        ref={ref as React.Ref<HTMLDivElement>}
        className={props.className}
        style={props.style}
      >
        {`${value as string}`}
      </DisplayValue>
    );
  }

  const onInteract = (e: React.PointerEvent | React.TouchEvent) => {
    const targetShape = editor.getShapeAtPoint(editor.inputs.currentPagePoint);
    if (
      (shape && targetShape && shape.id !== targetShape.id) ||
      activeCustomTool
    ) {
      e.preventDefault();
    } else {
      e.stopPropagation();
    }
  };

  return (
    <Input
      required={true}
      onContextMenu={(e) => e.stopPropagation()}
      {...preventBubble}
      {...mergeProps(props, {
        onPointerDown: onInteract,
        onTouchStart: onInteract,
      })}
      autoComplete="off"
      className={cn(
        activeCustomTool ? "pointer-events-none" : "pointer-events-auto",
        props.className,
      )}
      value={value}
      onValueChange={onValueChange}
      onChange={onChange}
      onKeyDown={onKeyDown}
      ref={ref as React.Ref<HTMLInputElement>}
    />
  );
};

interface CanvasNumberInputProps extends Omit<NumberInputProps, "className"> {
  "aria-label"?: string;
  className?: string;
  isCustomNodeParameter?: boolean;
}

export function CanvasNumberInput({
  ref,
  value,
  onValueChange,
  className,
  isCustomNodeParameter,
  ...props
}: CanvasNumberInputProps) {
  const isCustomNode = use(CustomNodeEditorContext) && !isCustomNodeParameter;
  const editor = useEditor();
  const shape = use(BlockContainerShapeContext);
  const { min, max, step } = props;
  // A plain function: the React Compiler memoises it, and a hand-written
  // useCallback here could not be preserved alongside it.
  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      return;
    }
    // Right and left step the value, the same as up and down (which
    // react-aria's NumberField already does). Devin, 9/16: every numeric
    // input on the canvas should nudge on all four arrows, and a dial is
    // never a place anyone wants to move a caret.
    if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
      const by = step ?? 1;
      const current = typeof value === "number" && Number.isFinite(value) ? value : (min ?? 0);
      let next = current + (event.key === "ArrowRight" ? by : -by);
      if (min !== undefined) next = Math.max(min, next);
      if (max !== undefined) next = Math.min(max, next);
      // Snap to the step's precision so 0.29 + 0.01 is 0.3, not 0.30000000000000004.
      const decimals = (String(by).split(".")[1] ?? "").length;
      next = Number(next.toFixed(decimals));
      event.preventDefault();
      if (next !== current) onValueChange?.(next);
    }
  };

  const isReadOnly = useIsReadonlyEditor();
  const activeCustomTool = useActiveCustomTool();

  if (isReadOnly) {
    return (
      <DisplayValue
        ref={ref as React.Ref<HTMLDivElement>}
        className={className}
        style={props.style as CSSProperties}
      >
        {value}
      </DisplayValue>
    );
  }

  const onInteract = (e: React.PointerEvent | React.TouchEvent) => {
    const targetShape = editor.getShapeAtPoint(editor.inputs.currentPagePoint);
    if (
      (shape && targetShape && shape.id !== targetShape.id) ||
      activeCustomTool
    ) {
      e.preventDefault();
    } else {
      e.stopPropagation();
    }
  };

  const Component = isCustomNode ? (Input as typeof NumberInput) : NumberInput;

  return (
    <Component
      ref={ref as React.Ref<HTMLInputElement>}
      isRequired={true}
      // onContextMenu={(e) => e.stopPropagation()}
      {...preventBubble}
      {...mergeProps(props, {
        onPointerDown: onInteract,
        onTouchStart: onInteract,
        onKeyDown,
        onValueChange,
      })}
      className={cn(
        activeCustomTool ? "pointer-events-none" : "pointer-events-auto",
        className,
      )}
      value={value}
    />
  );
}

export function CanvasPercentageInput({
  ref,
  className,
  value,
  ...props
}: Omit<CanvasNumberInputProps, "min" | "max" | "step" | "formatOptions">) {
  const isReadOnly = useIsReadonlyEditor();

  if (isReadOnly) {
    return (
      <DisplayValue
        ref={ref as React.Ref<HTMLDivElement>}
        className={className}
        style={props.style as CSSProperties}
      >
        {typeof value === "number"
          ? percentFormatter.format(value || 0)
          : // If its a parameter, we need to just display the value
            value}
      </DisplayValue>
    );
  }

  return (
    <CanvasNumberInput
      {...props}
      ref={ref}
      className={className}
      value={value}
      min={0}
      max={1}
      step={0.01}
      formatOptions={{ style: "percent" }}
    />
  );
}

export const CanvasTextArea = function CanvasInput({
  ref,
  value,
  onChange,
  onValueChange: onValueChangeProp,
  ...props
}: TextareaProps) {
  const onValueChange = useCallback(
    (newValue: string) => onValueChangeProp?.(newValue),
    [onValueChangeProp],
  );

  const isReadOnly = useIsReadonlyEditor();
  const activeCustomTool = useActiveCustomTool();
  const editor = useEditor();
  const shape = use(BlockContainerShapeContext);

  if (isReadOnly) {
    return (
      <DisplayValue
        ref={ref as unknown as React.Ref<HTMLDivElement>}
        className={cn(props.className, "h-auto whitespace-pre-wrap")}
        style={props.style}
        truncate={false}
      >
        {`${value as string}`}
      </DisplayValue>
    );
  }

  const onInteract = (e: React.PointerEvent | React.TouchEvent) => {
    const targetShape = editor.getShapeAtPoint(editor.inputs.currentPagePoint);
    if (shape && targetShape && shape.id !== targetShape.id) {
      e.preventDefault();
    } else {
      e.stopPropagation();
    }
  };

  return (
    <Textarea
      onContextMenu={(e) => e.stopPropagation()}
      required={true}
      {...preventBubble}
      {...mergeProps(props, {
        onPointerDown: onInteract,
        onTouchStart: onInteract,
      })}
      className={cn(
        activeCustomTool ? "pointer-events-none" : "pointer-events-auto",
        props.className,
      )}
      value={value}
      onValueChange={onValueChange}
      onChange={onChange}
      ref={ref as unknown as React.RefObject<HTMLTextAreaElement>}
    />
  );
};

export const CanvasButton = function CanvasButton({
  className,
  isTrigger,
  alwaysEnabled,
  ...props
}: ButtonProps & { isTrigger?: boolean; alwaysEnabled?: boolean }) {
  const isReadOnly = useIsReadonlyEditor();
  const activeCustomTool = useActiveCustomTool();
  const editor = useEditor();
  const shape = use(BlockContainerShapeContext);

  const onInteract = (e: React.PointerEvent | React.TouchEvent) => {
    const targetShape = editor.getShapeAtPoint(editor.inputs.currentPagePoint);

    if (shape && targetShape && shape.id !== targetShape.id) {
      e.preventDefault();
    } else {
      e.stopPropagation();
    }
  };

  return (
    <Button
      {...mergeProps(preventBubble, props, {
        onPointerDown: isTrigger ? preventBubble.onPointerDown : onInteract,
        onTouchStart: isTrigger ? preventBubble.onTouchStart : onInteract,
      })}
      disabled={props.disabled || (isReadOnly && !alwaysEnabled)}
      className={cn(
        "flex items-center justify-center gap-2 rounded-md px-3 py-2 text-sm ring-offset-background placeholder:text-slate-dim focus-visible:outline-hidden focus-visible:ring-2  focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
        activeCustomTool ? "pointer-events-none" : "pointer-events-auto",
        className,
      )}
    />
  );
};

export const CanvasToggle = function CanvasButton({
  ref,
  className,
  ...props
}: ToggleProps) {
  const isReadOnly = useIsReadonlyEditor();
  const activeCustomTool = useActiveCustomTool();
  const editor = useEditor();
  const shape = use(BlockContainerShapeContext);

  const onInteract = (e: React.PointerEvent | React.TouchEvent) => {
    const targetShape = editor.getShapeAtPoint(editor.inputs.currentPagePoint);
    if (shape && targetShape && shape.id !== targetShape.id) {
      e.preventDefault();
    } else {
      e.stopPropagation();
    }
  };

  return (
    <Toggle
      {...preventBubble}
      {...mergeProps(props, {
        onPointerDown: onInteract,
        onTouchStart: onInteract,
      })}
      ref={ref}
      className={cn(
        "text-slate-dim stroke-1",
        activeCustomTool || isReadOnly
          ? "pointer-events-none"
          : "pointer-events-auto",
        className,
      )}
    />
  );
};

export function OperatorSelect({
  value,
  onValueChange,
  validOperators,
}: {
  value: string;
  onValueChange: (v: string) => void;
  validOperators?: ComparisonOperator[];
}) {
  return (
    <CanvasSimpleSelect
      value={value}
      onValueChange={onValueChange}
      options={comparisonOperatorOptions.filter((o) =>
        validOperators ? validOperators.includes(o.value) : true,
      )}
      aria-label="Comparison Operator"
    />
  );
}

export function PercentOperatorSelect({
  value,
  onValueChange,
}: {
  value: string;
  onValueChange: (v: string) => void;
}) {
  return (
    <CanvasSimpleSelect
      aria-label="Comparison Operator"
      tooltip="Comparison operator, typically >= for similarity."
      value={value}
      options={percentComparisonOperatorOptions}
      onValueChange={onValueChange}
    />
  );
}

export function FieldSelect({
  value,
  onValueChange,
  className,
  validOperators,
}: {
  value: string;
  onValueChange: (v: string) => void;
  className?: string;
  validOperators?: ComparisonOperator[];
}) {
  // A field whose NAME does not settle what it means says so here. The two date fields are
  // the reason this exists: "Post Created Date" and "Post Arrival Date" both sound like the
  // time the post was made, and choosing the wrong one produces a feed that looks broken
  // rather than one that errors. The label alone cannot carry that, and the option list is
  // not the place for a paragraph -- so the explanation follows the SELECTED field.
  const hint = fieldOptions.find((o) => o.value === value)?.hint;

  return (
    <CanvasSimpleSelect
      // Named for what it is to the person choosing it, not for how it is stored.
      // Every option here already reads as plain English ("User Followers"); the
      // underlying dotted path is an implementation detail and used to be shown
      // beside each option, which taught people a syntax they never have to type.
      tooltip={hint ?? "The attribute of the post to use"}
      className={cn(className, "shrink-0")}
      value={value}
      placeholder="Field"
      onValueChange={onValueChange}
      options={fieldOptions.filter((o) =>
        validOperators ? o.validOperators === validOperators : true,
      )}
      aria-label="Record Field"
    />
  );
}

export function CanvasSwitch({
  checked,
  onCheckedChange,
  disabled,
  // Radix renders a <button>, so it cannot be wrapped in a <label> to name it. Forwarded rather
  // than left off: every other control in this file names itself, and a switch whose only label is
  // an adjacent <span> is unreachable by name.
  "aria-label": ariaLabel,
}: {
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
  disabled?: boolean;
  "aria-label"?: string;
}) {
  const isReadOnly = useIsReadonlyEditor();
  const activeCustomTool = useActiveCustomTool();
  const editor = useEditor();
  const shape = use(BlockContainerShapeContext);

  const onInteract = (e: React.PointerEvent | React.TouchEvent) => {
    const targetShape = editor.getShapeAtPoint(editor.inputs.currentPagePoint);
    if (shape && targetShape && shape.id !== targetShape.id) {
      e.preventDefault();
    } else {
      e.stopPropagation();
    }
  };

  return (
    <Switch
      checked={checked}
      onCheckedChange={onCheckedChange}
      disabled={disabled}
      aria-label={ariaLabel}
      {...preventBubble}
      className={cn(
        activeCustomTool || isReadOnly
          ? "pointer-events-none"
          : "pointer-events-auto",
      )}
      onPointerDown={onInteract}
      onTouchStart={onInteract}
    />
  );
}
